// src/components/modals/TestReportModal.tsx - Detailed Test Execution Report
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface TestReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    agentName: string;
}

interface TestExecution {
    id: string;
    agent_name: string;
    test_name: string;
    test_type: string;
    status: 'PASSED' | 'FAILED' | 'ERROR' | 'RUNNING';
    agent_output: string;
    expected_outcome: string;
    validation_analysis: string;
    ai_confidence: number;
    execution_time_ms: number;
    response_time_ms: number;
    failure_reason?: string;
    tokens_used: number;
    cost_estimate: number;
    completed_at: string;
    started_at: string;
}

const TestReportModal: React.FC<TestReportModalProps> = ({
    isOpen,
    onClose,
    agentName
}) => {
    const [testExecutions, setTestExecutions] = useState<TestExecution[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedExecution, setSelectedExecution] = useState<TestExecution | null>(null);
    const [reportFilter, setReportFilter] = useState<'all' | 'latest' | 'passed' | 'failed'>('latest');

    useEffect(() => {
        if (isOpen) {
            loadTestExecutions();
        }
    }, [isOpen, agentName, reportFilter]);

    const loadTestExecutions = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.get(`${API_BASE_URL}/db/test-executions`);
            let executions = response.data.filter((exec: TestExecution) =>
                exec.agent_name === agentName && exec.status !== 'RUNNING'
            );

            // Apply filtering
            if (reportFilter === 'latest') {
                // Get latest execution for each test case
                const latestExecutions = new Map<string, TestExecution>();
                executions.forEach((exec: TestExecution) => {
                    const testName = exec.test_name;
                    if (!latestExecutions.has(testName) ||
                        new Date(exec.completed_at || exec.started_at) >
                        new Date(latestExecutions.get(testName)!.completed_at || latestExecutions.get(testName)!.started_at)) {
                        latestExecutions.set(testName, exec);
                    }
                });
                executions = Array.from(latestExecutions.values());
            } else if (reportFilter === 'passed') {
                executions = executions.filter((exec: TestExecution) => exec.status === 'PASSED');
            } else if (reportFilter === 'failed') {
                executions = executions.filter((exec: TestExecution) => exec.status === 'FAILED' || exec.status === 'ERROR');
            }

            // Sort by completion time (most recent first)
            executions.sort((a, b) =>
                new Date(b.completed_at || b.started_at).getTime() -
                new Date(a.completed_at || a.started_at).getTime()
            );

            setTestExecutions(executions);
        } catch (error) {
            console.error('Error loading test executions:', error);
            setError('Failed to load test execution reports');
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusColor = (status: string) => ({
        'PASSED': '#28a745',
        'FAILED': '#dc3545',
        'ERROR': '#fd7e14',
        'RUNNING': '#007bff'
    }[status] || '#6c757d');

    const getStatusBadge = (status: string) => (
        <span style={{
            padding: '0.25rem 0.5rem',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            backgroundColor: getStatusColor(status),
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem'
        }}>
            {status === 'PASSED' && ''}
            {status === 'FAILED' && ''}
            {status === 'ERROR' && ''}
            {status === 'RUNNING' && ''}
            {status}
        </span>
    );

    const calculateSummary = () => {
        const total = testExecutions.length;
        const passed = testExecutions.filter(e => e.status === 'PASSED').length;
        const failed = testExecutions.filter(e => e.status === 'FAILED' || e.status === 'ERROR').length;
        const avgConfidence = total > 0 ? testExecutions.reduce((sum, e) => sum + (e.ai_confidence || 0), 0) / total : 0;
        const totalCost = testExecutions.reduce((sum, e) => sum + (e.cost_estimate || 0), 0);
        const totalTokens = testExecutions.reduce((sum, e) => sum + (e.tokens_used || 0), 0);

        return { total, passed, failed, avgConfidence, totalCost, totalTokens };
    };

    const handleExportReport = () => {
        const summary = calculateSummary();
        let reportContent = `Test Execution Report - ${agentName}\n`;
        reportContent += `Generated: ${new Date().toLocaleString()}\n`;
        reportContent += `Filter: ${reportFilter}\n\n`;

        reportContent += `=== SUMMARY ===\n`;
        reportContent += `Total Tests: ${summary.total}\n`;
        reportContent += `Passed: ${summary.passed}\n`;
        reportContent += `Failed: ${summary.failed}\n`;
        reportContent += `Success Rate: ${summary.total > 0 ? ((summary.passed / summary.total) * 100).toFixed(1) : 0}%\n`;
        reportContent += `Avg AI Confidence: ${(summary.avgConfidence * 100).toFixed(1)}%\n`;
        reportContent += `Total Cost: $${summary.totalCost.toFixed(4)}\n`;
        reportContent += `Total Tokens: ${summary.totalTokens}\n\n`;

        reportContent += `=== DETAILED RESULTS ===\n`;
        testExecutions.forEach((exec, index) => {
            reportContent += `${index + 1}. ${exec.test_name}\n`;
            reportContent += `   Status: ${exec.status}\n`;
            reportContent += `   Type: ${exec.test_type}\n`;
            reportContent += `   AI Confidence: ${(exec.ai_confidence * 100).toFixed(1)}%\n`;
            reportContent += `   Execution Time: ${exec.execution_time_ms}ms\n`;
            reportContent += `   Expected: ${exec.expected_outcome}\n`;
            reportContent += `   Actual: ${exec.agent_output}\n`;
            if (exec.failure_reason) {
                reportContent += `   Failure Reason: ${exec.failure_reason}\n`;
            }
            reportContent += `   AI Analysis: ${exec.validation_analysis}\n`;
            reportContent += `   Completed: ${new Date(exec.completed_at || exec.started_at).toLocaleString()}\n\n`;
        });

        const blob = new Blob([reportContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `test_report_${agentName}_${reportFilter}_${new Date().toISOString().split('T')[0]}.txt`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    const summary = calculateSummary();

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}
                 style={{width: '95vw', maxWidth: '1400px', maxHeight: '90vh', overflow: 'auto'}}>

                {/* Header */}
                <div className="modal-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: '2px solid #dee2e6',
                    paddingBottom: '1rem'
                }}>
                    <h2> Test Execution Report - {agentName}</h2>
                    <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        <button
                            onClick={handleExportReport}
                            className="widget-button button-secondary"
                            disabled={isLoading || testExecutions.length === 0}
                        >
                            📄 Export Report
                        </button>
                        <button onClick={onClose} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer'}}>
                            ✕
                        </button>
                    </div>
                </div>

                {/* Filter Controls */}
                <div className="report-controls" style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px'
                }}>
                    <label style={{fontWeight: 'bold'}}>Show:</label>
                    <select
                        value={reportFilter}
                        onChange={(e) => setReportFilter(e.target.value as any)}
                        style={{padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc'}}
                    >
                        <option value="latest">Latest Results (Current Session)</option>
                        <option value="all">All Executions (Historical)</option>
                        <option value="passed">Passed Tests Only</option>
                        <option value="failed">Failed Tests Only</option>
                    </select>

                    <button
                        onClick={loadTestExecutions}
                        className="widget-button button-secondary"
                        disabled={isLoading}
                        style={{marginLeft: 'auto'}}
                    >
                        {isLoading ? ' Loading...' : ' Refresh'}
                    </button>
                </div>

                {isLoading ? (
                    <div style={{textAlign: 'center', padding: '3rem'}}>
                        <div style={{fontSize: '3rem', marginBottom: '1rem'}}></div>
                        <div>Loading test execution report...</div>
                    </div>
                ) : error ? (
                    <div style={{textAlign: 'center', padding: '3rem', color: '#dc3545'}}>
                        <div style={{fontSize: '3rem', marginBottom: '1rem'}}></div>
                        <div>{error}</div>
                    </div>
                ) : testExecutions.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '3rem', color: '#6c757d'}}>
                        <div style={{fontSize: '3rem', marginBottom: '1rem'}}></div>
                        <div>No test execution data found for {agentName}</div>
                        <div style={{fontSize: '0.9rem', marginTop: '0.5rem'}}>
                            Execute some tests first to generate a report.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Summary Stats */}
                        <div className="summary-stats" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: '1rem',
                            marginBottom: '2rem'
                        }}>
                            <div className="stat-card" style={{
                                padding: '1rem',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#1976d2'}}>
                                    {summary.total}
                                </div>
                                <div style={{color: '#666', fontSize: '0.8rem'}}>Total Tests</div>
                            </div>

                            <div className="stat-card" style={{
                                padding: '1rem',
                                backgroundColor: '#e8f5e8',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#2e7d32'}}>
                                    {summary.passed}
                                </div>
                                <div style={{color: '#666', fontSize: '0.8rem'}}>Passed</div>
                            </div>

                            <div className="stat-card" style={{
                                padding: '1rem',
                                backgroundColor: '#ffebee',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#d32f2f'}}>
                                    {summary.failed}
                                </div>
                                <div style={{color: '#666', fontSize: '0.8rem'}}>Failed</div>
                            </div>

                            <div className="stat-card" style={{
                                padding: '1rem',
                                backgroundColor: '#fff3e0',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#f57c00'}}>
                                    {summary.total > 0 ? ((summary.passed / summary.total) * 100).toFixed(1) : 0}%
                                </div>
                                <div style={{color: '#666', fontSize: '0.8rem'}}>Success Rate</div>
                            </div>

                            <div className="stat-card" style={{
                                padding: '1rem',
                                backgroundColor: '#fce4ec',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#c2185b'}}>
                                    {(summary.avgConfidence * 100).toFixed(1)}%
                                </div>
                                <div style={{color: '#666', fontSize: '0.8rem'}}>Avg Confidence</div>
                            </div>

                            <div className="stat-card" style={{
                                padding: '1rem',
                                backgroundColor: '#f1f8e9',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#689f38'}}>
                                    ${summary.totalCost.toFixed(3)}
                                </div>
                                <div style={{color: '#666', fontSize: '0.8rem'}}>Total Cost</div>
                            </div>
                        </div>

                        {/* Detailed Results Table */}
                        <div className="results-section">
                            <h3 style={{marginBottom: '1rem', color: '#333'}}>📈 Detailed Test Results</h3>

                            <div style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    maxHeight: '600px',
                                    overflow: 'auto'
                                }}>
                                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                        <thead style={{position: 'sticky', top: 0, backgroundColor: '#f8f9fa'}}>
                                            <tr>
                                                <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>Test Name</th>
                                                <th style={{padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Status</th>
                                                <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>Agent Output</th>
                                                <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>Expected</th>
                                                <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6'}}>AI Analysis</th>
                                                <th style={{padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6'}}>Confidence</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {testExecutions.map((execution, index) => (
                                                <tr
                                                    key={execution.id}
                                                    style={{
                                                        borderBottom: '1px solid #eee',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => setSelectedExecution(selectedExecution?.id === execution.id ? null : execution)}
                                                >
                                                    <td style={{padding: '0.75rem', fontWeight: '500'}}>
                                                        <div style={{marginBottom: '0.25rem'}}>{execution.test_name}</div>
                                                        <div style={{fontSize: '0.75rem', color: '#666'}}>
                                                            {execution.test_type} • {new Date(execution.completed_at || execution.started_at).toLocaleDateString()}
                                                        </div>
                                                    </td>
                                                    <td style={{padding: '0.75rem', textAlign: 'center'}}>
                                                        {getStatusBadge(execution.status)}
                                                    </td>
                                                    <td style={{padding: '0.75rem', maxWidth: '250px'}}>
                                                        <div style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {execution.agent_output}
                                                        </div>
                                                    </td>
                                                    <td style={{padding: '0.75rem', maxWidth: '200px'}}>
                                                        <div style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {execution.expected_outcome}
                                                        </div>
                                                    </td>
                                                    <td style={{padding: '0.75rem', maxWidth: '250px'}}>
                                                        <div style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {execution.validation_analysis}
                                                        </div>
                                                        {selectedExecution?.id === execution.id && (
                                                            <button
                                                                style={{
                                                                    marginTop: '0.5rem',
                                                                    padding: '0.25rem 0.5rem',
                                                                    fontSize: '0.75rem',
                                                                    backgroundColor: '#007bff',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    // Show full analysis
                                                                }}
                                                            >
                                                                View Full Analysis
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td style={{padding: '0.75rem', textAlign: 'center'}}>
                                                        <div style={{
                                                            fontSize: '1.1rem',
                                                            fontWeight: 'bold',
                                                            color: execution.ai_confidence > 0.8 ? '#28a745' : execution.ai_confidence > 0.6 ? '#ffc107' : '#dc3545'
                                                        }}>
                                                            {(execution.ai_confidence * 100).toFixed(0)}%
                                                        </div>
                                                        <div style={{fontSize: '0.75rem', color: '#666'}}>
                                                            {execution.execution_time_ms}ms
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Expanded Analysis Panel */}
                        {selectedExecution && (
                            <div style={{
                                marginTop: '2rem',
                                padding: '1.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #dee2e6'
                            }}>
                                <h4 style={{marginBottom: '1rem', color: '#333'}}>
                                     Detailed Analysis: {selectedExecution.test_name}
                                </h4>

                                <div style={{display: 'grid', gap: '1rem'}}>
                                    <div>
                                        <strong>Expected Outcome:</strong>
                                        <div style={{
                                            padding: '0.75rem',
                                            backgroundColor: '#e8f5e8',
                                            borderRadius: '4px',
                                            marginTop: '0.5rem',
                                            border: '1px solid #c3e6cb'
                                        }}>
                                            {selectedExecution.expected_outcome}
                                        </div>
                                    </div>

                                    <div>
                                        <strong>Agent Output:</strong>
                                        <div style={{
                                            padding: '0.75rem',
                                            backgroundColor: selectedExecution.status === 'PASSED' ? '#e8f5e8' : '#f8d7da',
                                            borderRadius: '4px',
                                            marginTop: '0.5rem',
                                            border: `1px solid ${selectedExecution.status === 'PASSED' ? '#c3e6cb' : '#f5c6cb'}`
                                        }}>
                                            {selectedExecution.agent_output}
                                        </div>
                                    </div>

                                    <div>
                                        <strong>AI Analysis:</strong>
                                        <div style={{
                                            padding: '0.75rem',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '4px',
                                            marginTop: '0.5rem',
                                            border: '1px solid #bbdefb',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}>
                                            {selectedExecution.validation_analysis}
                                        </div>
                                    </div>

                                    {selectedExecution.failure_reason && (
                                        <div>
                                            <strong>Failure Reason:</strong>
                                            <div style={{
                                                padding: '0.75rem',
                                                backgroundColor: '#fff3cd',
                                                borderRadius: '4px',
                                                marginTop: '0.5rem',
                                                border: '1px solid #ffeaa7',
                                                color: '#856404'
                                            }}>
                                                {selectedExecution.failure_reason}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                        gap: '1rem',
                                        marginTop: '1rem'
                                    }}>
                                        <div style={{textAlign: 'center'}}>
                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff'}}>
                                                {selectedExecution.execution_time_ms}ms
                                            </div>
                                            <div style={{fontSize: '0.8rem', color: '#666'}}>Execution Time</div>
                                        </div>
                                        <div style={{textAlign: 'center'}}>
                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745'}}>
                                                {selectedExecution.tokens_used}
                                            </div>
                                            <div style={{fontSize: '0.8rem', color: '#666'}}>Tokens Used</div>
                                        </div>
                                        <div style={{textAlign: 'center'}}>
                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#6f42c1'}}>
                                                ${selectedExecution.cost_estimate.toFixed(4)}
                                            </div>
                                            <div style={{fontSize: '0.8rem', color: '#666'}}>Cost</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Footer */}
                <div className="modal-actions" style={{
                    marginTop: '2rem',
                    borderTop: '1px solid #dee2e6',
                    paddingTop: '1rem'
                }}>
                    <button onClick={onClose}>Close</button>
                    {testExecutions.length > 0 && (
                        <button
                            onClick={handleExportReport}
                            className="widget-button button-primary"
                        >
                             Export Full Report
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestReportModal;